import { useTranslation } from 'react-i18next';
import { FileDownloadBox } from '@portal/ui-library';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import { useParams } from 'react-router';
import { downloadData } from 'utils/fetchDocument';
import { LegalFooter } from 'components/LegalFooter';
import { GreenLogo } from 'components/Logo';
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { EVENT_IDS } from 'pages/DigitalOrderPage/constants';

export const DoneStep = () => {
  const { t } = useTranslation('wizardDone');
  const { applicationId } = useParams();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const fileDownloadBoxRef = useRef<HTMLDivElement>(null);

  // Expose the ref to the window object for the skip to content button
  useEffect(() => {
    (window as any).fileDownloadBoxRef = fileDownloadBoxRef;

    // Cleanup when component unmounts
    return () => {
      delete (window as any).fileDownloadBoxRef;
    };
  }, []);

  // Change document title when component mounts
  useEffect(() => {
    const originalTitle = document.title;
    document.title = 'Antrag erfolgreich übermittelt - Landeskrankenhilfe';

    // Restore original title when component unmounts
    return () => {
      document.title = originalTitle;
    };
  }, []);

  const downloadFile = async (applicationId: string | undefined): Promise<void> => {
    setIsDownloading(true);
    setIsDone(false);
    try {
      if (applicationId) {
        const [response, responseAccessible] = await Promise.all([
          axios.get(`/api/public/new/application/${applicationId}/order`, {
            responseType: 'blob',
            params: {
              accessible: false
            }
          }),
          axios.get(`/api/public/new/application/${applicationId}/order`, {
            responseType: 'blob',
            params: {
              accessible: true
            }
          })
        ]);

        downloadData(response.data, `${applicationId}.pdf`);
        downloadData(responseAccessible.data, `${applicationId}-barrierefrei.pdf`);
      }
    } catch (error) {
      // TODO handle error
    } finally {
      setIsDownloading(false);
      setIsDone(true);
    }
  };

  return (
    <div className="grid-mx-res grid-my-res">
      <div className="s:grid s:grid-cols-12 s:pl-[40px] m:pl-[80px] pl-[16px] hidden">
        <div className="col-span-4">
          <GreenLogo />
        </div>
      </div>

      <div className="flex items-center justify-center w-full mb-[48px]">
        <div>
          <div className="flex justify-center">
            <CheckCircleOutlineOutlinedIcon
              sx={{ width: 40, height: 40 }}
              className="text-primary-80"
            />
          </div>
          <div className="text-center tracking-normal text-[20px] font-[700] leading-[28px] mt-[32px] mb-[8px]">
            {t('mainHeader')}
          </div>
          <div
            tabIndex={0}
            role="region"
            aria-label={
              'Sie haben den Antrag erfolgreich übermittelt. Laden Sie bitte hier Ihre Unterlagen herunter. In den nächsten Minuten erhalten Sie außerdem eine E-Mail, mit der wir Ihren Antragseingang bestätigen. Sobald Ihr Antrag bearbeitet wurde, melden wir uns bei Ihnen. Vielen Dank, Ihre Landeskrankenhilfe.'
            }
            className="text-center text-lkh-text-80 tracking-normal text-[0.875rem] font-[400] leading-[20px] mb-[32px]"
          >
            <p className="m-[0px]">{t('description.paragraph1')}</p>
            <p className="m-[0px]">{t('description.paragraph2')}</p>
            <p className="m-[0px]">{t('description.paragraph3')}</p>
          </div>

          <div ref={fileDownloadBoxRef} tabIndex={-1}>
            <p className="sr-only" id="contract-download">
              Bitte laden Sie die Antragsunterlagen herunter. Sie beinhalten den gestellten Antrag."
            </p>
            <FileDownloadBox
              id={EVENT_IDS.DOWNLOAD_APPLICATION}
              ariaDescribedBy="contract-download"
              isLoading={isDownloading}
              isDone={isDone}
              label={t('download.label')}
              onClick={() => downloadFile(applicationId)}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center">
        <div className="rounded-[4px] bg-surface-60 p-[24px] w-[992px]">
          <div className="text-center tracking-normal text-[16px] font-[700] leading-[24px] mb-[24px]">
            {t('contact.header')}
          </div>
          <div className="flex justify-between flex-col s:flex-row">
            <div
              tabIndex={0}
              role="button"
              aria-label={`${t('contact.location.title')}: ${t('contact.location.detail1')} ${t(
                'contact.location.detail2'
              )}`}
              className="focus:outline-none focus:ring-2 focus:ring-primary-60 focus:ring-offset-2 rounded p-[8px] -m-[8px] flex space-x-2 mb-s"
            >
              <div>
                <LocationOnIcon aria-hidden="true" />
              </div>
              <div>
                <div className="grid-cols-subgrid">
                  <div className="col-start-1 text-[12px] font-[700] leading-[18px] tracking-normal text-left inline-block align-middle">
                    {t('contact.location.title')}
                  </div>
                  <div className="col-start-2 text-text-60 text-[10px] font-[400] leading-[16px] tracking-normal text-left pt-s">
                    <p className="m-[0px]">{t('contact.location.detail1')}</p>
                    <p className="m-[0px]">{t('contact.location.detail2')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grow mb-s">
              <div className="flex s:justify-center">
                <div
                  tabIndex={0}
                  role="button"
                  aria-label={`${t('contact.number.title')}: ${t('contact.number.detail')}`}
                  className="focus:outline-none focus:ring-2 focus:ring-primary-60 focus:ring-offset-2 rounded p-[8px] -m-[8px] flex space-x-2"
                >
                  <div>
                    <CallIcon aria-hidden="true" />
                  </div>
                  <div>
                    <div className="grid-cols-subgrid">
                      <div className="col-start-1 text-[12px] font-[700] leading-[18px] tracking-normal text-left inline-block align-middle">
                        {t('contact.number.title')}
                      </div>
                      <div className="col-start-2 text-text-60 text-[10px] font-[400] leading-[16px] tracking-normal text-left pt-s">
                        {t('contact.number.detail')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              tabIndex={0}
              role="button"
              aria-label={`${t('contact.email.title')}: ${t('contact.email.title')}`}
              className="focus:outline-none focus:ring-2 focus:ring-primary-60 focus:ring-offset-2 rounded p-[8px] -m-[8px] flex space-x-2 mb-s"
            >
              <div>
                <EmailIcon aria-hidden="true" />
              </div>
              <div>
                <div className="grid-cols-subgrid">
                  <div className="col-start-1 text-[12px] font-[700] leading-[18px] tracking-normal text-left inline-block align-middle">
                    <a
                      className="text-text-80"
                      href={`mailto:${t('contact.email.title')}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('contact.email.title')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center grid-my-res">
        <a rel="noopener noreferrer" href="https://www.lkh.de/zusatzversicherung/">
          {t('footer.navigation')}
        </a>
      </div>
      <div className="fixed left-0 bottom-0 w-full">
        <LegalFooter />
      </div>
    </div>
  );
};
